import logger from "@/lib/logger";
import { MetaHandler } from "@/main/common/MetaHandler";
import { MobileShellRuntime } from "@/main/mobile-shell/MobileShellRuntime";

// Tells the Android shell which language the member picked IN THE APP, so the strings it draws natively (the
// notification channel names in phone settings, the cold-start offline panel) follow it instead of the phone's
// language (FI-3449). Built on a default ⚑AT1: the in-app language is the one source of truth; there is no system
// per-app language picker to keep in sync.
//
// Design: designs/04-live/2026-07-22-mobile-shell-native-seam-reference.md §9.
//
// Sent at boot and after every soft remount (`bootstrapFrontEnd.ts`), deduped by the last tag sent: a language
// change is a page reload or a `?lang=` navigation, so one of the two always sees the new tag. Reached through
// `window.Capacitor.Plugins` (the seam reference's §1 rule). No feature flag (rule 35): on the web, on iOS and on
// any APK built before the plugin, `Plugins.SeavienAppLanguage` is `undefined` and this is inert. No event (rule
// 37): nothing here is a member action.

type SeavienAppLanguagePlugin = {
    setAppLanguage?: (options: { tag: string }) => Promise<void>;
};

/** The Java `PLUGIN_NAME`; `validate-shell.mjs` (`assertAppLanguagePluginIsWiredThrough`) asserts they agree. */
export const APP_LANGUAGE_PLUGIN_NAME = "SeavienAppLanguage";

/** One stable marker for the present-plugin failure path; the absent-plugin path stays silent. */
export const APP_LANGUAGE_CALL_FAILED_MARKER = "SEAVIEN_APP_LANGUAGE_CALL_FAILED";

let lastSentTag: string | null = null;

function getPlugin(): SeavienAppLanguagePlugin | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }
    const capacitor = (window as unknown as {
        Capacitor?: { Plugins?: Record<string, SeavienAppLanguagePlugin | undefined> };
    }).Capacitor;
    return capacitor?.Plugins?.[APP_LANGUAGE_PLUGIN_NAME];
}

/** Fire-and-forget: never awaited on a startup or navigation path. */
export function syncNativeAppLanguage(): void {
    if (!MobileShellRuntime.isNativeShell()) {
        return;
    }
    const plugin = getPlugin();
    const setAppLanguage = plugin?.setAppLanguage;
    if (!setAppLanguage) {
        return;
    }
    const tag = MetaHandler.getCurrentLanguageTag();
    if (!tag || tag === lastSentTag) {
        return;
    }
    lastSentTag = tag;
    Promise.resolve()
        .then(() => setAppLanguage.call(plugin, { tag }))
        .catch((error: unknown) => {
            // Forget it, so the next remount tries again.
            if (lastSentTag === tag) {
                lastSentTag = null;
            }
            logger.warn(`${APP_LANGUAGE_CALL_FAILED_MARKER} ${APP_LANGUAGE_PLUGIN_NAME}.setAppLanguage failed`, error);
        });
}

/** Tests only. */
export function resetAppLanguageBridgeForTests(): void {
    lastSentTag = null;
}
